import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { FlaskConical, RefreshCw, Plus, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

interface SolicitudLaboratorio {
  id: string;
  laboratorioId: string;
  laboratorioRazonSocial: string;
  acreditacionEma: string | null;
  normaCodigo: string;
  alcance: string;
  vigenciaInicio: string;
  vigenciaFin: string | null;
  parVigente: boolean;
  estatus: string;
  motivoAsignacion: string | null;
  motivoSustitucion: string | null;
  fechaAsignacion: string;
  fechaSustitucion: string | null;
}

interface Disponible {
  normaLaboratorioId: string;
  laboratorioRazonSocial: string;
  acreditacionEma: string | null;
  alcance: string;
  vigenciaInicio: string;
  vigenciaFin: string | null;
}

const ESTADOS_PERMITIDOS = new Set([
  "ALCANCE_TECNICO_DETERMINADO",
  "MUESTRA_REQUERIDA",
  "REPORTE_REQUERIDO",
  "EVALUACION_TECNICA",
  "ACLARACION_TECNICA",
  "INFORMACION_INCOMPLETA",
]);

/**
 * Sección integrada al detalle de Solicitud Telecom para mostrar y mutar el
 * laboratorio acreditado asignado. Fase 2b — consume `v_laboratorio_norma_activo`
 * vía el endpoint sectorial y respeta la regla RN-203/204 desde el backend.
 */
export function LaboratorioAsignadoSection({
  solicitudId,
  estadoCodigo,
}: {
  solicitudId: string;
  estadoCodigo: string | null;
}) {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const activo = useQuery({
    queryKey: ["solicitud-lab", solicitudId],
    queryFn: async () => {
      const { data, status } = await api.get<SolicitudLaboratorio | "">(
        `/api/sector/telecom/solicitudes/${solicitudId}/laboratorio`,
        { validateStatus: (s) => s === 200 || s === 204 },
      );
      return status === 204 ? null : (data as SolicitudLaboratorio);
    },
    enabled: !!solicitudId,
  });

  const historial = useQuery({
    queryKey: ["solicitud-lab", solicitudId, "historial"],
    queryFn: async () =>
      (await api.get<SolicitudLaboratorio[]>(
        `/api/sector/telecom/solicitudes/${solicitudId}/laboratorio/historial`,
      )).data,
    enabled: !!solicitudId,
  });

  const puedeAsignar = !!estadoCodigo && ESTADOS_PERMITIDOS.has(estadoCodigo);

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-fg">
          <FlaskConical className="mr-1 inline h-4 w-4" /> Laboratorio asignado
        </h2>
        {puedeAsignar && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1 rounded bg-primary px-2.5 py-1.5 text-sm text-white hover:opacity-90"
          >
            {activo.data ? (
              <>
                <RefreshCw className="h-4 w-4" /> Cambiar laboratorio
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" /> Asignar laboratorio
              </>
            )}
          </button>
        )}
      </div>

      {!puedeAsignar && (
        <p className="rounded bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mr-1 inline h-4 w-4" />
          La asignación de laboratorio está habilitada en los estados de análisis técnico
          (ALCANCE_TECNICO_DETERMINADO, MUESTRA_REQUERIDA, REPORTE_REQUERIDO, EVALUACION_TECNICA,
          ACLARACION_TECNICA, INFORMACION_INCOMPLETA). Estado actual:{" "}
          <strong>{estadoCodigo ?? "sin flujo"}</strong>.
        </p>
      )}

      {activo.data ? (
        <div className="rounded border bg-surface-2 p-3 text-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-medium text-fg">{activo.data.laboratorioRazonSocial}</div>
              <div className="text-xs text-fg-muted">
                {activo.data.acreditacionEma ?? "Sin acreditación EMA registrada"} · NOM{" "}
                {activo.data.normaCodigo}
              </div>
            </div>
            <div className="text-right">
              {activo.data.parVigente ? (
                <span className="rounded bg-estado-ok-bg px-2 py-0.5 text-xs font-medium text-estado-ok">
                  Vigente
                </span>
              ) : (
                <span className="rounded bg-estado-warn-bg px-2 py-0.5 text-xs font-medium text-estado-warn">
                  Fuera de vigencia / suspendido
                </span>
              )}
            </div>
          </div>
          <p className="mt-2 text-sm text-fg-muted">{activo.data.alcance}</p>
          <div className="mt-2 text-xs text-fg-subtle">
            Vigencia del par: {activo.data.vigenciaInicio} → {activo.data.vigenciaFin ?? "—"} · Asignado{" "}
            {new Date(activo.data.fechaAsignacion).toLocaleString("es-MX")}
          </div>
          {activo.data.motivoAsignacion && (
            <p className="mt-2 rounded bg-surface p-2 text-xs text-fg-muted">
              <span className="font-medium">Motivo: </span>{activo.data.motivoAsignacion}
            </p>
          )}
        </div>
      ) : activo.isLoading ? (
        <p className="text-sm text-fg-subtle">Cargando…</p>
      ) : (
        <p className="rounded border border-dashed p-4 text-center text-sm text-fg-subtle">
          No hay laboratorio asignado todavía.
        </p>
      )}

      {historial.data && historial.data.length > 1 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-xs font-medium text-fg-muted">
            Historial ({historial.data.length - 1} sustitución{historial.data.length - 1 === 1 ? "" : "es"})
          </summary>
          <ul className="mt-2 divide-y rounded border bg-surface">
            {historial.data
              .filter((h) => h.estatus === "SUSTITUIDO")
              .map((h) => (
                <li key={h.id} className="p-2 text-xs">
                  <div className="flex justify-between">
                    <span className="font-medium text-fg">{h.laboratorioRazonSocial}</span>
                    <span className="text-fg-subtle">
                      {h.fechaSustitucion && new Date(h.fechaSustitucion).toLocaleString("es-MX")}
                    </span>
                  </div>
                  <div className="text-fg-muted">
                    {h.normaCodigo} — {h.alcance}
                  </div>
                  {h.motivoSustitucion && (
                    <div className="mt-1 text-fg-muted">
                      <span className="font-medium">Motivo sustitución: </span>{h.motivoSustitucion}
                    </div>
                  )}
                </li>
              ))}
          </ul>
        </details>
      )}

      {showModal && (
        <AsignarModal
          solicitudId={solicitudId}
          yaAsignado={!!activo.data}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            qc.invalidateQueries({ queryKey: ["solicitud-lab", solicitudId] });
          }}
        />
      )}
    </section>
  );
}

function AsignarModal({
  solicitudId,
  yaAsignado,
  onClose,
  onSuccess,
}: {
  solicitudId: string;
  yaAsignado: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [normaLaboratorioId, setNormaLaboratorioId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const disponibles = useQuery({
    queryKey: ["solicitud-lab", solicitudId, "disponibles"],
    queryFn: async () =>
      (await api.get<Disponible[]>(
        `/api/sector/telecom/solicitudes/${solicitudId}/laboratorios-disponibles`,
      )).data,
  });

  const asignar = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { normaLaboratorioId };
      if (motivo.trim()) body.motivo = motivo.trim();
      await api.post(`/api/sector/telecom/solicitudes/${solicitudId}/laboratorio`, body);
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) => {
      setError(err.response?.data?.detail ?? "Error al asignar laboratorio");
    },
  });

  const motivoObligatorio = yaAsignado;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl space-y-3 rounded-lg bg-surface p-4 shadow-overlay">
        <h3 className="text-lg font-semibold text-fg">
          {yaAsignado ? "Cambiar laboratorio" : "Asignar laboratorio"}
        </h3>
        <p className="text-sm text-fg-muted">
          Solo se muestran laboratorios cuyo par norma↔laboratorio está operable hoy y cubre la
          NOM/DT de la solicitud (RN-203 / RN-204).
        </p>

        {disponibles.isLoading && <p className="text-sm text-fg-subtle">Cargando opciones…</p>}
        {disponibles.data && disponibles.data.length === 0 && (
          <p className="rounded border border-estado-warn/25 bg-estado-warn-bg p-3 text-sm text-estado-warn">
            <AlertCircle className="mr-1 inline h-4 w-4" />
            No hay laboratorios acreditados disponibles para esta NOM/DT. Verifica el catálogo
            (Laboratorios) o solicita excepción autorizada.
          </p>
        )}
        {disponibles.data && disponibles.data.length > 0 && (
          <ul className="max-h-72 divide-y overflow-y-auto rounded border">
            {disponibles.data.map((d) => (
              <li key={d.normaLaboratorioId}>
                <label
                  className={`flex cursor-pointer items-start gap-2 p-3 hover:bg-surface-2 ${
                    normaLaboratorioId === d.normaLaboratorioId ? "bg-primary-tint" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="lab"
                    value={d.normaLaboratorioId}
                    checked={normaLaboratorioId === d.normaLaboratorioId}
                    onChange={(e) => setNormaLaboratorioId(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1 text-sm">
                    <div className="font-medium text-fg">{d.laboratorioRazonSocial}</div>
                    {d.acreditacionEma && (
                      <div className="text-xs text-fg-muted">EMA: {d.acreditacionEma}</div>
                    )}
                    <div className="mt-1 text-xs text-fg-muted">{d.alcance}</div>
                    <div className="mt-1 text-xs text-fg-subtle">
                      Vigencia: {d.vigenciaInicio} → {d.vigenciaFin ?? "—"}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}

        <label className="block text-sm">
          <span className="font-medium text-fg-muted">
            Motivo {motivoObligatorio && <span className="text-estado-err">*</span>}
          </span>
          <textarea
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={
              motivoObligatorio
                ? "Requerido al sustituir el laboratorio actual"
                : "Opcional"
            }
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>

        {error && (
          <div className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded border bg-surface px-3 py-1.5 text-sm hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            disabled={
              !normaLaboratorioId ||
              (motivoObligatorio && !motivo.trim()) ||
              asignar.isPending
            }
            onClick={() => {
              setError(null);
              asignar.mutate();
            }}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {yaAsignado ? "Sustituir" : "Asignar"}
          </button>
        </div>
      </div>
    </div>
  );
}
